import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Check, X as XIcon, Activity, RefreshCw, AlertTriangle } from 'lucide-react';
import { adminFetch } from './shared';

export const HealthTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastCheck, setLastCheck] = useState(null);

  const fetchHealth = async () => {
    setLoading(true);
    try {
      const res = await adminFetch('/api/admin/health');
      if (res.ok) {
        const d = await res.json();
        setData(d);
        setLastCheck(new Date());
      }
    } catch (e) {
      console.error('Health check failed:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 60000); // refresh every 60s
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading && !data) {
    return <div className="text-center py-12 text-slate-500">Verificando estado del sistema...</div>;
  }

  const overallStyles = {
    ok: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', label: 'Todo OK' },
    warn: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', label: 'Advertencias' },
    error: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', label: 'Hay errores' },
  };
  const ov = overallStyles[data?.overall] || overallStyles.warn;

  const statusIcon = (s) => {
    if (s === 'ok') return <Check className="w-4 h-4 text-emerald-600" />;
    if (s === 'warn') return <AlertTriangle className="w-4 h-4 text-amber-600" />;
    return <XIcon className="w-4 h-4 text-rose-600" />;
  };
  const statusColor = (s) => s === 'ok' ? 'bg-emerald-50 border-emerald-200' : s === 'warn' ? 'bg-amber-50 border-amber-200' : 'bg-rose-50 border-rose-200';

  return (
    <div className="space-y-4" data-testid="health-tab">
      {/* Overall summary */}
      <Card className={`${ov.bg} ${ov.border} border-2`}>
        <div className="p-4 sm:p-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center ${ov.text}`}>
              <Activity className="w-6 h-6" />
            </div>
            <div>
              <p className={`text-lg font-bold ${ov.text}`}>{ov.label}</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {data?.checks?.filter(c => c.status === 'ok').length || 0} de {data?.checks?.length || 0} servicios funcionando
              </p>
            </div>
          </div>
          <button
            onClick={fetchHealth}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 bg-white rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors"
            data-testid="health-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Actualizar</span>
          </button>
        </div>
      </Card>

      {/* Individual checks */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {data?.checks?.map((c) => (
          <Card key={c.name} className={`${statusColor(c.status)} border`} data-testid={`health-check-${c.name.replace(/[^a-zA-Z0-9]/g, '-')}`}>
            <div className="p-3.5 flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center shrink-0">
                {statusIcon(c.status)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-800">{c.name}</p>
                <p className="text-xs text-slate-600 mt-0.5 break-words">{c.detail}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Footer info */}
      <div className="text-center text-[11px] text-slate-400 pt-2">
        Última verificación: {lastCheck?.toLocaleTimeString('es-MX') || '—'} · Auto-refresh cada 60s · {data?.elapsed_ms}ms
      </div>
    </div>
  );
};
